import React, { useContext, useState } from "react";
import classNames from "classnames";
import { NoCounterpartyStatsRow } from "../../../common/contracts/counterparty.contract";
import { numberFormatter } from "../../../common/helpers/number";
import { pluralize } from "../../../common/helpers/string";
import {
    CounterpartyDirectionLabels,
    GENERAL_COUNTERPARTY_LABEL,
} from "../../../common/helpers/counterparties";
import {
    GridTableFallbacks,
    GridTableHeader,
} from "../../general/Tables/GridTable/GridTable";
import { DirectorySort } from "../../../common/constants/counterparty";
import { SortableHeader } from "../../general/SortableHeader/SortableHeader";
import { CollapsibleHeader } from "../../general/Collapsible/CollapsibleHeader";
import { DirectoryContext } from "./Directory.context";
import styles from "./DirectoryTable.module.scss";
import { NoCounterpartyStatsItem } from "./NoCounterpartyStatsItem";

interface Props {
    transactionsCount?: number;
    stats?: NoCounterpartyStatsRow[];
    isLoading: boolean;
    showRevenue: boolean;
    showSpend: boolean;
}

export const NoCounterpartyStats: React.FC<Props> = ({
    transactionsCount,
    stats,
    isLoading,
    showRevenue,
    showSpend,
}) => {
    const {
        counterpartyDirection: direction,
        noCounterpartySort: sort,
        setNoCounterpartySort: setSort,
    } = useContext(DirectoryContext);
    const directionLabel = direction
        ? CounterpartyDirectionLabels[direction]
        : GENERAL_COUNTERPARTY_LABEL;

    const [opened, setOpened] = useState(false);

    if (!transactionsCount) {
        return null;
    }

    return (
        <>
            <CollapsibleHeader
                className={classNames(styles.collapsibleHeader, {
                    [styles.collapsibleHeaderOpened]: opened,
                })}
                isOpened={opened}
                onToggle={() => setOpened(!opened)}
            >
                {numberFormatter.format(transactionsCount)}{" "}
                {pluralize("transaction", transactionsCount)} with no{" "}
                {directionLabel?.toLowerCase()}
            </CollapsibleHeader>
            {opened && (
                <>
                    <GridTableHeader className={styles.row} columnBorders>
                        <div className={styles.categoryWithoutCounterparty}>
                            <SortableHeader
                                sort={sort}
                                asc={DirectorySort.CATEGORY_ASC}
                                desc={DirectorySort.CATEGORY_DESC}
                                onChange={setSort}
                                defaultOrder="asc"
                            >
                                Category
                            </SortableHeader>
                        </div>
                        <div>
                            <SortableHeader
                                sort={sort}
                                asc={DirectorySort.NUMBER_OF_TRANSACTIONS_ASC}
                                desc={DirectorySort.NUMBER_OF_TRANSACTIONS_DESC}
                                onChange={setSort}
                                defaultOrder="desc"
                            >
                                #
                            </SortableHeader>
                        </div>
                        {showRevenue && (
                            <div>
                                <SortableHeader
                                    sort={sort}
                                    asc={DirectorySort.REVENUE_ASC}
                                    desc={DirectorySort.REVENUE_DESC}
                                    onChange={setSort}
                                    defaultOrder="desc"
                                >
                                    Revenue
                                </SortableHeader>
                            </div>
                        )}
                        {showSpend && (
                            <div>
                                <SortableHeader
                                    sort={sort}
                                    asc={DirectorySort.SPEND_ASC}
                                    desc={DirectorySort.SPEND_DESC}
                                    onChange={setSort}
                                    defaultOrder="desc"
                                >
                                    Spend
                                </SortableHeader>
                            </div>
                        )}
                    </GridTableHeader>
                    {stats?.map((row, index) => (
                        <NoCounterpartyStatsItem
                            key={row.categoryId}
                            row={row}
                            showRevenue={showRevenue}
                            showSpend={showSpend}
                            isLast={index === stats.length - 1}
                        />
                    ))}
                    <GridTableFallbacks
                        loading={isLoading}
                        dataCount={stats?.length}
                        showLoading
                        className={classNames(styles.row, styles.lastRow)}
                    />
                </>
            )}
        </>
    );
};
